import axios from 'axios';
import { useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useToast } from '../../../../utils/useToast';

const apiHost = () => process.env.REACT_APP_API_HOST;

export const connectionsQueryKey = 'integrationConnections';

export type Provider =
  | 'google-calendar'
  | 'outlook-calendar'
  | 'google-workspace'
  | 'slack';

export const PROVIDER_NAMES: Record<Provider, string> = {
  'google-calendar': 'Google Calendar',
  'outlook-calendar': 'Outlook Calendar',
  'google-workspace': 'Google Workspace',
  slack: 'Slack',
};

export type Connection = {
  id: string;
  provider: Provider;
  level: 'user' | 'company';
  own: boolean;
  status: 'active' | 'needs_reconnect';
  accountEmail: string | null;
  lastError: string | null;
  connectedAt: string | null;
  settings: {
    calendarId?: string | null;
    teamCalendar?: boolean;
    teamCalendarId?: string | null;
    hideLeaveTypes?: boolean;
    slackChannel?: string | null;
    slackTeamName?: string | null;
    lastSync?: {
      at: string;
      created: number;
      updated: number;
      unchanged: number;
      invited: number;
      skipped: number;
      failed: number;
    } | null;
  };
};

export type ConnectionsData = {
  connections: Connection[];
  teamCalendar: {
    connectionId: string;
    accountEmail: string | null;
    status: string;
  } | null;
  available: Record<Provider, boolean>;
};

export type CalendarOption = { id: string; name: string; primary: boolean };

export type PreviewRow = {
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: string | null;
  department: string | null;
  phone: string | null;
  action: 'create' | 'update' | 'unchanged' | 'conflict' | 'invite';
  changes: string[];
};

export type Preview = {
  users: PreviewRow[];
  counts: Record<string, number>;
};

export type ImportResult = {
  created: number;
  updated: number;
  unchanged: number;
  invited: number;
  skipped: number;
  failed: number;
  errors: Array<{ email: string; message: string }>;
};

export function errorMessage(err: any, fallback: string) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || err?.message || fallback;
}

export async function fetchConnections(): Promise<ConnectionsData> {
  const response = await axios.get(`${apiHost()}/v1/integrations/connections`);
  return response.data?.data;
}

export function useConnections() {
  return useQuery(connectionsQueryKey, fetchConnections, {
    staleTime: 60 * 1000,
  });
}

/** Fetches the provider's authorisation URL with the session token, then goes there. */
export async function startConnect(
  provider: Provider,
  returnTo: 'settings' | 'profile' = 'settings',
) {
  const query = returnTo === 'profile' ? '?returnTo=profile' : '';
  const response = await axios.get(
    `${apiHost()}/v1/integrations/${provider}/auth-url${query}`,
  );
  const url = response.data?.data?.url;
  if (!url) {
    throw new Error(
      response.data?.message || 'No authorisation URL was returned.',
    );
  }
  window.location.assign(url);
}

export async function disconnect(id: string) {
  await axios.delete(`${apiHost()}/v1/integrations/connections/${id}`);
}

export async function updateConnection(
  id: string,
  body: Partial<{
    calendarId: string | null;
    teamCalendar: boolean;
    teamCalendarId: string | null;
    hideLeaveTypes: boolean;
  }>,
) {
  const response = await axios.patch(
    `${apiHost()}/v1/integrations/connections/${id}`,
    body,
  );
  return response.data?.data as Connection;
}

export async function fetchCalendars(id: string): Promise<CalendarOption[]> {
  const response = await axios.get(
    `${apiHost()}/v1/integrations/connections/${id}/calendars`,
  );
  return response.data?.data || [];
}

export async function previewWorkspace(): Promise<Preview> {
  const response = await axios.post(
    `${apiHost()}/v1/integrations/google-workspace/sync`,
  );
  return response.data?.data;
}

export async function importWorkspace(body: {
  sendInvites: boolean;
}): Promise<ImportResult> {
  const response = await axios.post(
    `${apiHost()}/v1/integrations/google-workspace/import`,
    body,
  );
  return response.data?.data;
}

export async function setTeamsWebhook(url: string) {
  await axios.put(`${apiHost()}/v1/integrations/teams/webhook`, { url });
}

export async function clearTeamsWebhook() {
  await axios.delete(`${apiHost()}/v1/integrations/teams/webhook`);
}

const CALLBACK_MESSAGES: Record<string, string> = {
  access_denied: 'You cancelled the connection.',
  invalid_state: 'The connection link expired or was already used. Try again.',
  admin_required: 'Only admins can connect this integration.',
  not_workspace_admin:
    'That Google account is not a Google Workspace admin, so it cannot read the directory.',
  missing_code: 'The provider did not complete the connection.',
};

/**
 * OAuth callbacks come back to /settings or /profile with
 * ?integration=<provider>&status=success|error&message=... Shows the outcome
 * once and removes those parameters from the URL.
 */
export function useIntegrationCallbackToast() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get('integration') as Provider | null;
    const status = params.get('status');
    if (!provider || !status) return;

    const name = PROVIDER_NAMES[provider] || provider.replace(/-/g, ' ');
    const message = params.get('message');
    toast(
      status === 'success'
        ? {
            title: 'Connected',
            description: `${name} is now connected.`,
            variant: 'success',
          }
        : {
            title: 'Connection failed',
            description:
              (message && (CALLBACK_MESSAGES[message] || message)) ||
              'The provider did not complete the connection.',
            variant: 'error',
          },
    );

    ['integration', 'status', 'message'].forEach((key) => params.delete(key));
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}`,
    );
    queryClient.invalidateQueries(connectionsQueryKey);
    // Run once, on arrival from the callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
